import { validate as uuidValidate } from 'uuid';
import { useCallback, useState } from 'react';
import { Order, OrderType } from '../../../../clients/quipli-service/order/order';
import { isOrderCustomInput, OrderInputItem } from '../../../../clients/quipli-service/order/order-calculation';
import { ProductPrice, RentalProductPrice } from '../../../../clients/quipli-service/pricing/pricing';
import { Location } from '../../../../clients/quipli-service/location/location';
import { usePricingApiV1 } from '../../../../clients/quipli-service/pricing/pricing-api-v1';
import { toast } from '../../../../util/toasts';
import { ShippingType, toShippingType } from '../../../../clients/quipli-service/delivery/delivery';
import { Currency } from 'dinero.js';
import { ProductType } from '../../../../clients/quipli-service/product/product';
import { convertToMoney } from '../../../../util/util';
import { prepareUpdateOrderInputs } from '../utils/order-management-utils';
import { UserAddress } from '../../../../clients/quipli-service/user/user';
import {
  FinancialSummary,
  ProductFeeLineItem,
  CustomProductLineItem,
  ProductLineItem,
} from '../../../../clients/quipli-service/financials/financials';
import { useOrderApiV1 } from '../../../../clients/quipli-service/order/order-api-v1';
import { TaxBreakdown } from '../../../../clients/quipli-service/tax/tax';

interface UseOrderManagementProps {
  order: Order;
  location: Location;
  summary?: FinancialSummary;
}

interface UseOrderManagementReturn {
  orderInputs: OrderInputItem[];
  productPrices: Map<string, ProductPrice[]>;
  initProductPrices: () => Promise<void>;
  updateProduct: (orderInputItem: OrderInputItem) => void;
  deleteOrderProduct: (orderProductId: string) => void;
  addOrderProduct: (newInput: OrderInputItem) => Promise<void>;
  cancelOrderManagementChanges: () => void;
  submitOrder: (
    billingAddress: UserAddress | undefined,
    deliveryAddress: UserAddress | undefined,
    selectedDelivery: ShippingType,
    taxBreakdown: TaxBreakdown[] | undefined,
    taxCode: { id?: string; name?: string; taxCodeSource?: string } | undefined,
    coupon: string,
  ) => Promise<void>;
}

export const useOrderManagement = ({ order, location, summary }: UseOrderManagementProps): UseOrderManagementReturn => {
  const { listPrices } = usePricingApiV1();
  const { updateOrder } = useOrderApiV1();

  const [orderInputs, setOrderInputs] = useState<OrderInputItem[]>([]);
  const [productPrices, setProductPrices] = useState<Map<string, ProductPrice[]>>(new Map());
  const [prevOrderInputs, setPrevOrderInputs] = useState<OrderInputItem[]>([]);

  const findOriginalRentalPrice = (
    price: RentalProductPrice,
    allPrices: ProductPrice[],
  ): RentalProductPrice | undefined => {
    if (price.type !== OrderType.Rental || !price.orderId) {
      return undefined;
    }

    return allPrices.find(
      (p) =>
        p.type === OrderType.Rental &&
        !p.orderId &&
        p.durationValue === price.durationValue &&
        p.durationUnit === price.durationUnit,
    ) as RentalProductPrice;
  };

  const addOrderProduct = async (newInput: OrderInputItem) => {
    if (newInput.type === OrderType.Rental) {
      const productPrice = await listPrices({
        productIds: [newInput.productId],
        locationId: location.id,
        orderId: order.id,
      });

      setProductPrices((prev) => {
        const rpp = productPrice as RentalProductPrice[];
        const pricesWithOriginals = rpp.map((price) => ({
          ...price,
          originalPrice: findOriginalRentalPrice(price, productPrice),
        }));

        const overriddenIds = pricesWithOriginals.map((p) => p.originalPrice?.id).filter((id) => !!id) as string[];
        const newMap = new Map(prev);

        newMap.set(
          newInput.id,
          pricesWithOriginals
            .filter((p) => overriddenIds.indexOf(p.id) < 0)
            .sort((a, b) => a.durationMinutes - b.durationMinutes),
        );

        return newMap;
      });
    }

    setOrderInputs((prev) => [...prev, newInput]);
  };

  const updateProduct = useCallback((orderInputItem: OrderInputItem) => {
    setOrderInputs((prev) => prev.map((line) => (line.id === orderInputItem.id ? orderInputItem : line)));
  }, []);

  const deleteOrderProduct = useCallback(
    (orderProductId: string) => {
      setOrderInputs((prev) => {
        const filteredInputs = prev.filter((input) => input.id !== orderProductId);

        if (order.isCycleBilled && !filteredInputs.some((input) => input.type === OrderType.Rental)) {
          toast('At least one rental product is required for cycle billed orders', 'error');
          return prev; // Don't update if validation fails
        }

        if (filteredInputs.length === 0) {
          toast('At least one product is required', 'error');
          return prev; // Don't update if validation fails
        }

        return filteredInputs;
      });
    },
    [order],
  );

  const initProductPrices = async () => {
    const currency = location.currency as Currency;

    const prodPrices: Map<string, ProductPrice[]> = new Map();

    for (const op of order.products) {
      const _prices = await listPrices({
        productIds: [op.product.id],
        locationId: location.id,
        orderId: order.id,
        userId: order.user.id,
        orderProductId: op.id,
      });

      const pricesWithOriginals = (_prices as RentalProductPrice[]).map((price) => ({
        ...price,
        originalPrice: findOriginalRentalPrice(price, _prices),
      }));
      const overriddenIds = pricesWithOriginals.map((p) => p.originalPrice?.id).filter((id) => !!id) as string[];

      prodPrices.set(
        `${op.id}`,
        pricesWithOriginals
          .filter((p) => overriddenIds.indexOf(p.id) < 0 && (p.orderProductId == op.id || !p.orderProductId))
          .sort((a, b) => a.durationMinutes - b.durationMinutes),
      );
    }

    setProductPrices(prodPrices);
    // Set order inputs
    const inputs = order.products.map((op) => {
      return {
        // Request props
        id: `${op.id}`,
        type: op.type,
        productId: op.product.id,
        customPrice: convertToMoney(op.customerBuyingPrice, currency).getAmount(),
        quantity: op.quantity,
        manualDiscount: op.discountPercentage,
        deliveryType: toShippingType(op.shippingTypeId),
        customOrderRentalRates:
          op.type === OrderType.Rental ? (prodPrices.get(`${op.id}`) as RentalProductPrice[] | undefined) : undefined,
        // rentalPriceRates,

        ...(op.type === OrderType.Rental && { start: op.rentalStartDate, end: op.rentalEndDate }),
        ...(op.product.type === ProductType.Custom && {
          name: op.name,
          description: op.description,
          taxable: !op.isTaxFree,
          generalLedgerAccountId: op.generalLedgerAccountId,
          taxCategoryId: op.taxCategoryId,
          isCustom: true,
        }),

        // Calculation props
        // deliveryOptions: deliveryOptions.get(op.product.id) || [],
        assignedUnits: op.assignedUnits.map((unit) => unit.id),
      } as OrderInputItem;
    });
    setOrderInputs(inputs);
    setPrevOrderInputs(inputs);
  };

  const cancelOrderManagementChanges = () => {
    setOrderInputs(prevOrderInputs);
  };

  // TODO: This still needs to badly be split up, but don't want to do it in this sprint to reduce risk of regression
  // since it's such a crucial function
  const submitOrder = async (
    billingAddress: UserAddress | undefined,
    deliveryAddress: UserAddress | undefined,
    selectedDelivery: ShippingType,
    taxBreakdown: TaxBreakdown[] | undefined,
    taxCode: { id?: string; name?: string; taxCodeSource?: string } | undefined,
    coupon: string,
  ) => {
    if (!summary) {
      toast('Cannot submit order. Please try again.', 'error');
      return;
    }

    const {
      cartTotal,
      orderDelivery,
      delivery,
      orderPickup,
      pickup,
      damageWaiver,
      couponDiscount,
      tax,
      locationFees,
      discount,
      orderSecurityDeposit,
      securityDepositPrice,
      existingProductFees,
    } = prepareUpdateOrderInputs(summary, location, order);

    await updateOrder(order.id, {
      documentType: order.documentType,
      locationId: location.id,
      billingAddressId: billingAddress?.id,
      deliveryAddressId: deliveryAddress?.id,
      deliveryType: selectedDelivery,
      currency: summary.total.getCurrency(),
      cartTotal: cartTotal.getAmount(),
      damageWaiver: damageWaiver
        ? {
            amount: damageWaiver.amount.getAmount(),
            hasCustomAmount: damageWaiver.hasCustomAmount,
          }
        : undefined,
      pickup: {
        amount: pickup?.getAmount() || 0,
        hasCustomAmount: orderPickup?.hasCustomAmount || false,
      },
      delivery: {
        amount: delivery?.getAmount() || 0,
        hasCustomAmount: orderDelivery?.hasCustomAmount || false,
      },
      tax: {
        taxCode: tax.taxCodeId,
        amount: tax.amount.getAmount(),
        hasCustomAmount: tax.hasCustomAmount,
        hasCustomPercentage: tax.hasCustomPercentage,
        percentage: tax.percentage,
        breakdown: taxBreakdown,
      },
      coupon: !couponDiscount.isZero()
        ? {
            code: order.user.tagPricing?.name || coupon,
            amount: couponDiscount.multiply(-1).getAmount(),
            isVip: false,
          }
        : undefined,
      additionalFees: locationFees.map((fee) => {
        const orderFee = order.additionalFees.find((f) => f.feeId === fee.feeId);

        return {
          orderFeeId: orderFee?.id,
          feeId: fee.feeId,
          amount: fee.amount.getAmount(),
          hasCustomAmount: fee.hasCustomAmount,
        };
      }),
      additionalDiscount: !discount.isZero() ? discount.multiply(-1).getAmount() : undefined,
      total: summary.total.getAmount(),
      googleCalculation: summary.googleCalculation,
      securityDeposit: !securityDepositPrice.isZero()
        ? {
            amount: securityDepositPrice.getAmount(),
            percentage: orderSecurityDeposit?.percentage || 0,
          }
        : undefined,
      products: orderInputs.map((input) => {
        if (isOrderCustomInput(input)) {
          const customLineItem = summary.lineItems.find(
            (line) => line.type === 'CustomProduct' && line.id === input.id,
          )! as CustomProductLineItem;

          return {
            uniqueId: uuidValidate(input.id) ? '' : input.id,
            productId: input.productId,
            name: input.name,
            description: input.description,
            taxable: input.taxable,
            generalLedgerAccountId: input.generalLedgerAccountId,
            taxCategoryId: input.taxCategoryId,
            type: input.type,
            amount: customLineItem.unitPrice.getAmount(),
            hasCustomAmount: true,
            quantity: input.quantity,
            isCustom: true,
            start: input.type == OrderType.Rental ? input.start : undefined,
            end: input.type == OrderType.Rental && input.end ? input.end : undefined,
            customOrderRentalRates: input.type == OrderType.Rental ? input.customOrderRentalRates : undefined,
            equipmentOwner: input.equipmentOwner,
          };
        }

        const productLineItem = summary.lineItems.find(
          (line) => line.type === 'Product' && line.id === input.id,
        )! as ProductLineItem;

        return {
          uniqueId: uuidValidate(input.id) ? '' : input.id,
          productId: input.productId,
          type: input.type,
          amount: productLineItem.unitPrice.getAmount(),
          hasCustomAmount: productLineItem.hasCustomAmount,
          pickup:
            summary.lineItems
              .find((line) => line.type === 'ProductPickup' && line.productLineItemId === input.id)
              ?.amount?.getAmount() || 0,
          delivery:
            summary.lineItems
              .find((line) => line.type === 'ProductDelivery' && line.productLineItemId === input.id)
              ?.amount?.getAmount() || 0,
          securityDeposit: summary.lineItems
            .find((line) => line.type === 'ProductSecurityDeposit' && line.productLineItemId === input.id)
            ?.amount?.getAmount(),
          quantity: input.quantity,
          additionalFees: summary.lineItems
            .filter((line) => line.type === 'ProductFee' && line.productLineItemId === input.id)
            .map((line) => {
              const fee = line as ProductFeeLineItem;
              const orderProductFee = existingProductFees.find(
                (f) => f.attributeId === fee.productFeeId && `${f.orderProductId}` === input.id,
              );

              return {
                orderFeeId: orderProductFee?.id,
                feeId: fee.productFeeId,
                amount: fee.amount.getAmount(),
                hasCustomAmount: fee.hasCustomAmount,
              };
            }),
          discount: input.manualDiscount,
          start: input.type == OrderType.Rental ? input.start : undefined,
          end: input.type == OrderType.Rental && input.end ? input.end : undefined,
          customOrderRentalRates:
            input.type == OrderType.Rental
              ? input.customOrderRentalRates?.filter((rr) => rr.orderId || rr.orderProductId || rr.action !== 'remove')
              : undefined,
          taxCode: { ...taxCode, id: taxCode?.id !== 'taxJar' ? taxCode?.id : undefined },
          options: [],
          services: [],
          isCustom: false,
        };
      }),
    });
  };

  return {
    orderInputs,
    productPrices,
    cancelOrderManagementChanges,
    initProductPrices,
    updateProduct,
    deleteOrderProduct,
    addOrderProduct,
    submitOrder,
  };
};
